import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { Customer } from '../../../../modals/product.modals';
import {
    PaymentData,
    PaymentDialogConfig,
} from '../../interfaces/payment-dialog.interface';

@Component({
  selector: 'app-payment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputNumberModule,
    AutoCompleteModule,
    InputTextModule,
  ],
  templateUrl: './payment-dialog.component.html',
  styleUrls: ['./payment-dialog.component.scss'],
})
export class PaymentDialogComponent implements OnInit {
  @Input() visible: boolean = false;
  @Input() config: PaymentDialogConfig = {};
  @Input() paymentData: PaymentData = { totalAmount: 0 };
  @Input() isProcessing: boolean = false;
  @Input() customers: Customer[] = []; // Customer list for autocomplete

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() paymentProcess = new EventEmitter<PaymentData>();
  @Output() paymentDataChange = new EventEmitter<PaymentData>();

  activePaymentTab: 'Cash' | 'Card' | 'UPI' | 'Redeem Points' | 'Split' =
    'Cash';
  isPartPaymentCash: boolean = false;
  isPartPaymentCard: boolean = false;
  isPartPaymentUPI: boolean = false;

  // Customer autocomplete
  filteredCustomers: Customer[] = [];

  ngOnInit() {
    // Initialize default values
    if (!this.paymentData.splitPaymentAmounts) {
      this.paymentData.splitPaymentAmounts = { cash: 0, card: 0, upi: 0 };
    }
  }

  setActivePaymentTab(
    tab: 'Cash' | 'Card' | 'UPI' | 'Redeem Points' | 'Split',
  ) {
    this.activePaymentTab = tab;
    this.resetPartPaymentStates();

    // Clear other payment amounts when switching to individual tabs
    // Only Split tab should allow multiple payment methods
    if (tab === 'Cash') {
      this.paymentData.cardAmount = 0;
      this.paymentData.upiAmount = 0;
      this.paymentData.redeemPointsAmount = 0;
    } else if (tab === 'Card') {
      this.paymentData.cashAmount = 0;
      this.paymentData.upiAmount = 0;
      this.paymentData.redeemPointsAmount = 0;
    } else if (tab === 'UPI') {
      this.paymentData.cashAmount = 0;
      this.paymentData.cardAmount = 0;
      this.paymentData.redeemPointsAmount = 0;
    } else if (tab === 'Redeem Points') {
      this.paymentData.cashAmount = 0;
      this.paymentData.cardAmount = 0;
      this.paymentData.upiAmount = 0;
    }
    // For Split tab, keep all amounts as they are

    this.emitPaymentDataChange();
  }

  resetPartPaymentStates() {
    this.isPartPaymentCash = false;
    this.isPartPaymentCard = false;
    this.isPartPaymentUPI = false;
  }

  isAnyPartPaymentEnabled(): boolean {
    return (
      this.isPartPaymentCash || this.isPartPaymentCard || this.isPartPaymentUPI
    );
  }

  onPartPaymentToggle(paymentType: 'Cash' | 'Card' | 'UPI', enabled: boolean) {
    if (enabled) {
      switch (paymentType) {
        case 'Cash':
          this.isPartPaymentCash = true;
          this.isPartPaymentCard = false;
          this.isPartPaymentUPI = false;
          break;
        case 'Card':
          this.isPartPaymentCash = false;
          this.isPartPaymentCard = true;
          this.isPartPaymentUPI = false;
          break;
        case 'UPI':
          this.isPartPaymentCash = false;
          this.isPartPaymentCard = false;
          this.isPartPaymentUPI = true;
          break;
      }

      if (
        this.activePaymentTab === 'Split' ||
        this.activePaymentTab === 'Redeem Points'
      ) {
        this.setActivePaymentTab(paymentType);
      }
    } else {
      switch (paymentType) {
        case 'Cash':
          this.isPartPaymentCash = false;
          break;
        case 'Card':
          this.isPartPaymentCard = false;
          break;
        case 'UPI':
          this.isPartPaymentUPI = false;
          break;
      }
    }
  }

  getPaidAmount(): number {
    if (this.activePaymentTab === 'Split') {
      return (
        (this.paymentData.splitPaymentAmounts?.cash || 0) +
        (this.paymentData.splitPaymentAmounts?.card || 0) +
        (this.paymentData.splitPaymentAmounts?.upi || 0)
      );
    } else {
      return (
        (this.paymentData.cashAmount || 0) +
        (this.paymentData.cardAmount || 0) +
        (this.paymentData.upiAmount || 0) +
        (this.paymentData.redeemPointsAmount || 0)
      );
    }
  }

  getBalanceAmount(): number {
    // Use rounded total for balance calculation to match the displayed total
    return Math.abs(this.getRoundedTotal() - this.getPaidAmount());
  }

  getBalanceLabel(): string {
    const balance = this.getRoundedTotal() - this.getPaidAmount();
    return balance < 0 ? 'Refund Amount' : 'Balance Amount';
  }

  isRefundScenario(): boolean {
    return this.getRoundedTotal() - this.getPaidAmount() < 0;
  }

  getTipAmount(): number {
    return this.paymentData.tipAmount || 0;
  }

  getRoundedTotal(): number {
    return Math.round(this.paymentData.totalAmount);
  }

  getCurrentPartPaymentAmount(): number {
    // Implementation for part payment amount calculation
    return 0;
  }

  getRemainingCreditAmount(): number {
    // Implementation for remaining credit amount calculation
    return 0;
  }

  // Numeric Keypad Methods
  addToAmount(digit: string) {
    if (this.activePaymentTab === 'Cash') {
      const currentValue = (this.paymentData.cashAmount || 0).toString();
      const newValue = currentValue === '0' ? digit : currentValue + digit;
      this.paymentData.cashAmount = parseFloat(newValue) || 0;
    } else if (this.activePaymentTab === 'UPI') {
      const currentValue = (this.paymentData.upiAmount || 0).toString();
      const newValue = currentValue === '0' ? digit : currentValue + digit;
      this.paymentData.upiAmount = parseFloat(newValue) || 0;
    } else if (this.activePaymentTab === 'Card') {
      const currentValue = (this.paymentData.cardAmount || 0).toString();
      const newValue = currentValue === '0' ? digit : currentValue + digit;
      this.paymentData.cardAmount = parseFloat(newValue) || 0;
    } else if (this.activePaymentTab === 'Redeem Points') {
      const currentValue = (
        this.paymentData.redeemPointsAmount || 0
      ).toString();
      const newValue = currentValue === '0' ? digit : currentValue + digit;
      this.paymentData.redeemPointsAmount = parseFloat(newValue) || 0;
    }
    this.emitPaymentDataChange();
  }

  backspaceAmount() {
    if (this.activePaymentTab === 'Cash') {
      const currentValue = (this.paymentData.cashAmount || 0).toString();
      const newValue = currentValue.slice(0, -1);
      this.paymentData.cashAmount = parseFloat(newValue) || 0;
    } else if (this.activePaymentTab === 'UPI') {
      const currentValue = (this.paymentData.upiAmount || 0).toString();
      const newValue = currentValue.slice(0, -1);
      this.paymentData.upiAmount = parseFloat(newValue) || 0;
    } else if (this.activePaymentTab === 'Card') {
      const currentValue = (this.paymentData.cardAmount || 0).toString();
      const newValue = currentValue.slice(0, -1);
      this.paymentData.cardAmount = parseFloat(newValue) || 0;
    } else if (this.activePaymentTab === 'Redeem Points') {
      const currentValue = (
        this.paymentData.redeemPointsAmount || 0
      ).toString();
      const newValue = currentValue.slice(0, -1);
      this.paymentData.redeemPointsAmount = parseFloat(newValue) || 0;
    }
    this.emitPaymentDataChange();
  }

  clearAmount() {
    if (this.activePaymentTab === 'Cash') {
      this.paymentData.cashAmount = 0;
    } else if (this.activePaymentTab === 'UPI') {
      this.paymentData.upiAmount = 0;
    } else if (this.activePaymentTab === 'Card') {
      this.paymentData.cardAmount = 0;
    } else if (this.activePaymentTab === 'Redeem Points') {
      this.paymentData.redeemPointsAmount = 0;
    } else if (this.activePaymentTab === 'Split') {
      this.paymentData.splitPaymentAmounts = { cash: 0, card: 0, upi: 0 };
    }
    this.emitPaymentDataChange();
  }

  onClose() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onProcessPayment() {
    this.paymentProcess.emit(this.paymentData);
  }

  private emitPaymentDataChange() {
    this.paymentDataChange.emit(this.paymentData);
  }

  onPaymentDataUpdate() {
    this.emitPaymentDataChange();
  }

  // Filter customers for autocomplete
  filterCustomers(event: any) {
    const query = event.query.toLowerCase();
    this.filteredCustomers = this.customers.filter(
      (customer) =>
        customer.customer_name.toLowerCase().includes(query) ||
        customer.mobile_no.includes(query),
    );
  }

  // Handle customer selection from autocomplete
  onCustomerSelectFromAutocomplete(event: any) {
    const customer = event.value || event;
    if (customer && customer.customer_name) {
      this.paymentData.customerName = customer.customer_name;
      this.paymentData.mobileNumber = customer.mobile_no;
      this.emitPaymentDataChange();
    }
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    const isMainKeyboardNumber = charCode >= 48 && charCode <= 57;
    const isNumpadNumber = charCode >= 96 && charCode <= 105;
    const isSpecialKey = [8, 9, 13, 46, 37, 38, 39, 40].includes(charCode);

    if (!isMainKeyboardNumber && !isNumpadNumber && !isSpecialKey) {
      event.preventDefault();
    }
  }

  handleMobileNumberPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    const numericValue = pastedText.replace(/[^0-9]/g, '').substring(0, 10);

    this.paymentData.mobileNumber = numericValue;

    const target = event.target as HTMLInputElement;
    if (target) {
      target.value = numericValue;
    }
    this.emitPaymentDataChange();
  }

  onMobileNumberInput(event: any) {
    const numericValue = event.target.value.replace(/[^0-9]/g, '');
    if (event.target.value !== numericValue) {
      event.target.value = numericValue;
      this.paymentData.mobileNumber = numericValue;
      this.emitPaymentDataChange();
    }
  }
}
